import { PRODUCT_TYPE } from "features/Products/constants";
import { SHIPPING_TYPE } from "features/Cart/constants";
import { getCartOrderValue, getInvalidShippingTypeMessage, getShippingTypeOptions } from "features/Cart/shippingPolicy";
import { useGetShippingTypes } from "hooks/cart";
import { useCreateOrder } from "hooks/order";
import { t } from "i18next";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { notify } from "utils/helperFuncs";

const useModalCreateOrder = ({ data, form, shippingTypes: shippingTypesFromParent }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { handleCreateOrder, loading } = useCreateOrder();
  const history = useHistory();

  const isMissingPickupInfo =
    data?.shippingType === SHIPPING_TYPE.PICK_UP_COUNTER && !data?.shippingInfo?.pickupInfo?.receiver;

  // `useList` (cha) đã gọi `useGetShippingTypes` với CÙNG variables — gọi lại ở đây là trùng request.
  // Chỉ tự fetch khi cha chưa có (vd. dùng nơi khác không truyền `shippingTypes` xuống).
  const { data: shippingTypesFetched } = useGetShippingTypes({
    contactCityID: data?.shippingContact?.cityID,
    contactWardID: data?.shippingContact?.wardID,
    exportedWarehouseID: data?.exportedWarehouse?.id,
    subTotal: getCartOrderValue(data),
    skip: Boolean(shippingTypesFromParent) || !data,
  });
  const shippingTypes = shippingTypesFromParent ?? shippingTypesFetched;
  const shippingTypeOptions = getShippingTypeOptions(shippingTypes);

  const invalidShippingTypeOption = shippingTypeOptions?.find(
    (option) => option?.value === data?.shippingType && option?.disabled
  );

  const validateCreateOrder = () => {
    const items = data?.items ?? [];
    const sellerActive = data?.seller?.isActive;
    const issues = {
      invalidShippingType: Boolean(invalidShippingTypeOption),
      noItems: items.length === 0,
      outOfStock: items.some(
        (item) =>
          PRODUCT_TYPE.MATERIAL === item.product.productType && (item.quantity > item.inStock || item.inStock === null)
      ),
      outOfMoq: items.some(
        (item) => PRODUCT_TYPE.MATERIAL === item.product.productType && item.quantity < item.product.minOrderQuantity
      ),
    };

    // Giá trị đơn rớt xuống dưới ngưỡng (hoặc BE báo hết hỗ trợ zone/tuyến) sau khi đã chọn PTVC
    // không còn hợp lệ. Popup riêng: chỉ 1 nút hành động, không phải nút Tạo đơn.
    if (issues.invalidShippingType) {
      return {
        variant: "invalidShippingType",
        title: t("v2.cart.shippingPolicy.invalidShippingTypeTitle"),
        message: getInvalidShippingTypeMessage({ cart: data, shippingTypeName: invalidShippingTypeOption?.label }),
        hideConfirmButton: false,
        hideCancelButton: true,
        okeButtonLabel: t("v2.cart.shippingPolicy.editShippingTypeButton"),
      };
    }

    if (issues.noItems) {
      return { title: t("cart.notHasProduct"), hideConfirmButton: true };
    }

    if (issues.outOfStock && issues.outOfMoq) {
      return { title: t("cart.outOfStockAndMoq"), hideConfirmButton: false };
    }

    if (issues.outOfStock) {
      return { title: t("cart.outOfStock"), hideConfirmButton: false };
    }

    if (issues.outOfMoq) {
      return { title: t("cart.outOfMoq"), hideConfirmButton: false };
    }

    if (!sellerActive) {
      return { title: t("cart.inactiveSeller"), hideConfirmButton: true };
    }

    return { title: t("cart.createOrderValidateConfirmation"), hideConfirmButton: false };
  };

  const onOpenCreateOrder = async () => {
    try {
      await form.validateFields();
      setIsVisible(true);
    } catch (error) {
      // Field lỗi có thể nằm ngoài vùng nhìn thấy (bảng sản phẩm, khối bên phải) — phải cuộn tới và
      // báo ra, nếu không user bấm "Tạo đơn hàng" mà không thấy gì xảy ra.
      const firstErrorField = error?.errorFields?.[0];
      if (!firstErrorField) throw error;

      form.scrollToField(firstErrorField.name, { behavior: "smooth", block: "center" });
      notify.warning({ message: firstErrorField.errors?.[0] });
    }
  };

  const onCancel = () => setIsVisible(false);

  const onCreateOrder = async () => {
    try {
      await handleCreateOrder({ cartID: data?.id }).then((response) => {
        const { id: orderID } = response.data.order.create;
        notify.success({ message: t("cart.createOrderSuccess") });
        history.push({
          pathname: `/order/${orderID}`,
        });
        setIsVisible(false);
      });
    } catch (error) {
      notify.error({ message: error.message });
    }
  };

  return { validateCreateOrder, onOpenCreateOrder, isVisible, onCancel, onCreateOrder, loading, isMissingPickupInfo };
};

export default useModalCreateOrder;
